import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { RequiredLabel } from "../../utils/validators";
import { addSupplies } from '../../services/Supplies/Supplies';
import { getUnitMeasures } from "../../services/Other/GeneralQueries";

const initialState = {
    code: '',
    product_name: '',
    lot: '',
    pieces_per_lot: '',
    lot_cost: '',
    unitofmeasure_id: '',
    capacity_dimensions: '',
    supplier: '',
    unit_cost: '',
    price_per_ml: '',
};

const sanitizeDecimal = (value, maxDecimals) => {
    const v = String(value ?? '')
        .replace(/[^\d.]/g, '')        // solo dígitos y punto
        .replace(/(\..*)\./g, '$1');  // un solo punto

    const [intPart, decPart] = v.split('.');
    if (decPart === undefined) return intPart;
    return `${intPart}.${decPart.slice(0, maxDecimals)}`;
};

const formatThousands = (raw) => {
    const v = String(raw ?? '').replace(/,/g, '');
    if (!v) return '';
    const [intPart, decPart] = v.split('.');
    const intFmt = new Intl.NumberFormat('es-MX').format(Number(intPart || 0));
    if (decPart === undefined) return intFmt;
    return `${intFmt}.${decPart}`;
};

const toNumOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
};

function AddSuppliesModal({ show, onHide, onSaved }) {
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState(initialState);
    const [unitMeasures, setUnitMeasures] = useState([]);
    const [focusedMoney, setFocusedMoney] = useState(null); // 'lot_cost' | 'unit_cost' | 'price_per_ml'

    useEffect(() => {
        if (!show) return;
        (async () => {
            const units = await getUnitMeasures();
            console.log('Unidades de medida:', units);
            setUnitMeasures(units ?? []);
        })();
    }, [show]);

    const requiredMissing = useMemo(() => {
        const req = ['code', 'product_name', 'lot', 'pieces_per_lot', 'lot_cost', 'unitofmeasure_id', 'unit_cost'];
        return req.some((k) => String(formData[k] ?? '').trim() === '');
    }, [formData]);

    const closeAndReset = () => {
        setFormData(initialState);
        onHide?.();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'code') {
            setFormData((p) => ({ ...p, code: value.toUpperCase() }));
            return;
        }

        setFormData((p) => ({ ...p, [name]: value }));
    };

    const handleMoneyChange = (name, value, maxDecimals) => {
        const clean = sanitizeDecimal(value, maxDecimals);
        setFormData((p) => ({ ...p, [name]: clean }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (requiredMissing) {
            toast.error('Completa los campos requeridos.');
            return;
        }

        const payload = {
            code: formData.code.trim().toUpperCase(),
            product_name: formData.product_name.trim(),
            lot: toNumOrNull(formData.lot),
            pieces_per_lot: toNumOrNull(formData.pieces_per_lot),
            lot_cost: toNumOrNull(formData.lot_cost),
            unitofmeasure_id: Number(formData.unitofmeasure_id),
            capacity_dimensions: formData.capacity_dimensions.trim() || null,
            supplier: formData.supplier.trim() || null,
            unit_cost: toNumOrNull(formData.unit_cost),
            price_per_ml: toNumOrNull(formData.price_per_ml),
        };

        setSaving(true);
        try {
            const res = await addSupplies(payload);
            if (!res?.result) {
                toast.error(res?.message ?? 'No se pudo guardar el consumible.');
                return;
            }
            toast.success('Consumible guardado.');
            setFormData(initialState);
            onSaved?.();
        } catch {
            toast.error('Error al guardar el consumible.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={saving ? undefined : closeAndReset} centered size="lg">
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>Agregar consumible</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Row className="g-3">
                        <Col md={4}>
                            <Form.Label as={RequiredLabel} required>Clave</Form.Label>
                            <Form.Control
                                name="code"
                                value={formData.code}
                                onChange={handleInputChange}
                                placeholder="Ej. EMP-001"
                                disabled={saving}
                                style={{ textTransform: 'uppercase' }}
                            />
                        </Col>

                        <Col md={8}>
                            <Form.Label as={RequiredLabel} required>Nombre</Form.Label>
                            <Form.Control
                                name="product_name"
                                value={formData.product_name}
                                onChange={handleInputChange}
                                disabled={saving}
                            />
                        </Col>

                        <Col md={4}>
                            <Form.Label as={RequiredLabel} required>Lote</Form.Label>
                            <Form.Control
                                name="lot"
                                type="number"
                                step="0.001"
                                value={formData.lot}
                                onChange={handleInputChange}
                                disabled={saving}
                            />
                        </Col>

                        <Col md={4}>
                            <Form.Label as={RequiredLabel} required>Piezas por lote</Form.Label>
                            <Form.Control
                                name="pieces_per_lot"
                                type="number"
                                step="0.001"
                                value={formData.pieces_per_lot}
                                onChange={handleInputChange}
                                disabled={saving}
                            />
                        </Col>

                        <Col md={4}>
                            <Form.Label as={RequiredLabel} required>Costo por lote</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                    inputMode="decimal"
                                    value={focusedMoney === 'lot_cost' ? formData.lot_cost : formatThousands(formData.lot_cost)}
                                    onFocus={() => setFocusedMoney('lot_cost')}
                                    onBlur={() => setFocusedMoney(null)}
                                    onChange={(e) => handleMoneyChange('lot_cost', e.target.value, 2)}
                                    disabled={saving}
                                />
                            </InputGroup>
                        </Col>

                        <Col md={4}>
                            <Form.Label as={RequiredLabel} required>Unidad de Medida</Form.Label>
                            <Form.Select
                                name="unitofmeasure_id"
                                value={formData.unitofmeasure_id}
                                onChange={handleInputChange}
                                disabled={saving}
                            >
                                <option value="">Selecciona…</option>
                                {unitMeasures.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.name} ({u.abbreviation})
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>

                        <Col md={4}>
                            <Form.Label style={{ fontWeight: 'bold' }}>Capacidad de dimensiones</Form.Label>
                            <Form.Control
                                name="capacity_dimensions"
                                value={formData.capacity_dimensions}
                                onChange={handleInputChange}
                                disabled={saving}
                            />
                        </Col>

                        <Col md={4}>
                            <Form.Label style={{ fontWeight: 'bold' }}>Proveedor</Form.Label>
                            <Form.Control
                                name="supplier"
                                value={formData.supplier}
                                onChange={handleInputChange}
                                disabled={saving}
                            />
                        </Col>

                        <Col md={6}>
                            <Form.Label as={RequiredLabel} required>Costo por unidad</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                    inputMode="decimal"
                                    value={focusedMoney === 'unit_cost' ? formData.unit_cost : formatThousands(formData.unit_cost)}
                                    onFocus={() => setFocusedMoney('unit_cost')}
                                    onBlur={() => setFocusedMoney(null)}
                                    onChange={(e) => handleMoneyChange('unit_cost', e.target.value, 2)}
                                    disabled={saving}
                                />
                            </InputGroup>
                        </Col>

                        <Col md={6}>
                            <Form.Label style={{ fontWeight: 'bold' }}>Precio por ml</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                    inputMode="decimal"
                                    value={focusedMoney === 'price_per_ml' ? formData.price_per_ml : formatThousands(formData.price_per_ml)}
                                    onFocus={() => setFocusedMoney('price_per_ml')}
                                    onBlur={() => setFocusedMoney(null)}
                                    onChange={(e) => handleMoneyChange('price_per_ml', e.target.value, 4)}
                                    disabled={saving}
                                />
                            </InputGroup>
                        </Col>
                    </Row>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" type="button" onClick={closeAndReset} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button variant="primary" type="submit" disabled={saving || requiredMissing}>
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Guardando...
                            </>
                        ) : (
                            'Guardar'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default AddSuppliesModal;